/**
 * "Start from template" gallery. Opened from the Trackers toolbar; shows the
 * product/default templates (from the built-in catalog) and the user's
 * reusable saved templates in two clearly-labeled groups. Picking one creates
 * a fully-configured, empty board (structure only) and navigates to it.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@monksflow/monks-ui';

import {
  BUILTIN_TRACKER_TEMPLATES,
  trackerTemplatesListQueryOptions,
  type TemplateCard,
} from '@/entities/tracker-template';
import { FormDialog } from '@/shared/ui/FormDialog';
import { SearchInput } from '@/shared/ui/SearchInput';
import { useTranslation } from 'react-i18next';

import { TemplateCoverCard } from './components/TemplateCoverCard';
import { useCreateTrackerFromTemplate } from './hooks/useCreateTrackerFromTemplate';

function matches(query: string, name: string, description?: string | null): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return name.toLowerCase().includes(q) || (description ?? '').toLowerCase().includes(q);
}

export function TemplateGalleryDialog({
  open,
  onOpenChange,
  onBack,
  workspaceId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Returns to the chooser this gallery was opened from. The gallery is one
   * step of a flow rather than a screen of its own, so backing out of it steps
   * back rather than abandoning the whole thing; ESC and the corner dismiss
   * still end it outright.
   */
  onBack: () => void;
  workspaceId: string;
}) {
  const { t, i18n } = useTranslation('trackers');

  const fromTemplate = useCreateTrackerFromTemplate({ workspaceId });
  const templatesQuery = useQuery({
    ...trackerTemplatesListQueryOptions(workspaceId),
    enabled: open && Boolean(workspaceId),
  });

  const [searchInput, setSearchInput] = useState('');

  const builtinCards = useMemo<TemplateCard[]>(
    () =>
      BUILTIN_TRACKER_TEMPLATES.filter((b) =>
        matches(searchInput, b.displayName, b.description),
      ).map((builtin) => ({ source: 'builtin', id: `builtin:${builtin.key}`, builtin })),
    // `displayName` and `description` are locale-dependent getters, so the filter has to
    // re-run when the language changes even though neither is named here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchInput, i18n.language],
  );

  const userCards = useMemo<TemplateCard[]>(
    () =>
      (templatesQuery.data ?? [])
        .filter((t) => matches(searchInput, t.name, t.description))
        .map((template) => ({ source: 'user', id: template.id, template })),
    [templatesQuery.data, searchInput],
  );

  const pickTemplate = (card: TemplateCard) =>
    fromTemplate.pick(card, { onCreated: () => onOpenChange(false) });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('trackers.templateGalleryDialog.startFromATemplate')}
      className="max-w-3xl"
    >
      <FormDialog.Body>
        <div className="flex flex-col gap-5">
          <SearchInput
            aria-label={t('trackers.templateGalleryDialog.searchTemplatesLabel')}
            placeholder={t('trackers.templateGalleryDialog.searchTemplates')}
            value={searchInput}
            onChange={setSearchInput}
          />

          {fromTemplate.isPending ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Spinner className="size-4" />{' '}
              {t('trackers.templateGalleryDialog.creatingYourTracker')}
            </div>
          ) : (
            <>
              <TemplateGroup
                title={t('trackers.templateGalleryDialog.defaultTemplates')}
                cards={builtinCards}
                onPick={pickTemplate}
              />
              <TemplateGroup
                title={t('trackers.templateGalleryDialog.savedTemplates')}
                cards={userCards}
                onPick={pickTemplate}
                loading={templatesQuery.isPending}
                emptyLabel={
                  templatesQuery.isError
                    ? t('trackers.templateGalleryDialog.savedTemplatesCouldNotBeLoaded')
                    : t('trackers.templateGalleryDialog.noSavedTemplatesYetSave')
                }
              />
            </>
          )}
        </div>
      </FormDialog.Body>
      <FormDialog.Footer
        secondary={{ label: t('trackers.templateGalleryDialog.back'), onClick: onBack }}
      />
    </FormDialog>
  );
}

function TemplateGroup({
  title,
  cards,
  onPick,
  loading,
  emptyLabel,
}: {
  title: string;
  cards: TemplateCard[];
  onPick: (card: TemplateCard) => void;
  loading?: boolean;
  emptyLabel?: string;
}) {
  const { t } = useTranslation('trackers');

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {loading ? (
        <p className="py-4 text-sm text-muted-foreground">
          {t('trackers.templateGalleryDialog.loading')}
        </p>
      ) : cards.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">
          {emptyLabel ?? t('trackers.templateGalleryDialog.noTemplates')}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card) => (
            <TemplateCoverCard key={card.id} card={card} onPick={onPick} />
          ))}
        </div>
      )}
    </section>
  );
}
